import * as Sentry from "@sentry/react"

/**
 * Webapp error reporting (plan §13.1).
 *
 * Imported for its side effect by `main.tsx`, before React renders, so a throw
 * during the first render is still reported.
 *
 * **A no-op when `VITE_SENTRY_DSN` is unset**, which is the local and preview
 * case. Same contract as the backend's `lib/sentry.ts`: no branching at call
 * sites, no account needed to run the app.
 *
 * Errors only — `tracesSampleRate: 0`, no Session Replay. Replay is worth
 * revisiting at block 02, when there is a UI worth replaying and a user to
 * replay; on a free tier with neither, it spends quota on nothing.
 */

declare const __BUILD_SHA__: string

const dsn = import.meta.env["VITE_SENTRY_DSN"] as string | undefined

if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.PROD ? "production" : "development",

    // Set by the `define` in vite.config.ts, which prefers BUILD_SHA and falls
    // back to Cloudflare's CF_PAGES_COMMIT_SHA. Must match the `release` that
    // @sentry/vite-plugin uploads source maps under, or the frames stay
    // unmapped — which is why both read the same value.
    release: __BUILD_SHA__,

    tracesSampleRate: 0,
    sendDefaultPii: false,

    // Browser noise that is never actionable: extension injections, and the
    // ResizeObserver warning every SPA emits. Each one that gets through is a
    // "new error signature" alert that trains the reader to ignore the next.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
    denyUrls: [
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-extension:\/\//,
    ],
  })
}
